/**
 * Public gallery and blog paths. Individual galleries live at /gallery/<slug>.
 * Category filters live at /galleries/<slug>. Legacy /galleries/<old-album>/
 * stays a redirect and is never treated as one of these category pages.
 */

const categoryContent = require('./categoryContent');

const SITE = 'https://carmenlorraine.com';

const GALLERY_ID = {
  officeLifestyle: '1759589430060',
  businessEvents: '1759906679778',
  family: '1760032504044',
  yoga: '1760033354647',
  headshots: '1773053405139',
  empowerment: '1783175991935',
  accommodation: '1783338587714',
  pregnancy: '1783339766484',
  matric: '1783341621998',
  healer: '1783345582974',
  weddings: '1783345837785',
  sound: '1783253813332',
  breath: '1783254059528',
  realEstate: '1783257932203',
  hotel: '1783258087231',
  coach: '1783261678927',
  teens: '1784092174810',
  farm: '1784093051197',
  smallBusiness: '1784093677350',
  restaurants: '1784100393386',
  productLifestyle: '1784108646242',
};

const BUSINESS_CATEGORY_ID = '1759919725909';
const LEGACY_SLUG_TO_ID = {
  'business-headshots': BUSINESS_CATEGORY_ID,
  'business-and-headshots': BUSINESS_CATEGORY_ID,
};
const LEGACY_CATEGORY_MATCH = {
  'business-and-commercial': ['business-headshots', 'business-and-headshots'],
};

const SEMANTIC_IDS = {
  family: [GALLERY_ID.family, GALLERY_ID.teens, GALLERY_ID.matric],
  lifestyle: [
    GALLERY_ID.officeLifestyle,
    GALLERY_ID.productLifestyle,
    GALLERY_ID.yoga,
    GALLERY_ID.empowerment,
    GALLERY_ID.healer,
    GALLERY_ID.sound,
    GALLERY_ID.breath,
    GALLERY_ID.coach,
  ],
  natural: [GALLERY_ID.farm, GALLERY_ID.accommodation, GALLERY_ID.realEstate, GALLERY_ID.hotel],
  maternity: [GALLERY_ID.pregnancy],
  newborn: [GALLERY_ID.pregnancy],
  weddings: [GALLERY_ID.weddings],
};

const CATALOG_FILTERS = [
  { slug: 'portrait', name: 'Portrait', match: 'portrait' },
  { slug: 'events', name: 'Events', match: 'events' },
  { slug: 'property', name: 'Property', match: 'property' },
  { slug: 'wellness-community', name: 'Wellness Community', match: 'wellness-community' },
  { slug: 'travel-conservation', name: 'Travel & Conservation', match: 'travel-conservation' },
];

function copy(slug, name, intro) {
  return {
    slug,
    name,
    h1: name,
    title: `${name} | Cape Town Photographer`,
    description: intro.length > 155 ? `${intro.slice(0, 152).trim()}…` : intro,
    intro,
  };
}

const SEMANTIC_FILTERS = [
  copy('family', 'Family', 'Family photography in Cape Town by Carmen Lorraine. Portraits of families, children, teens, and celebrations.'),
  copy('business-and-commercial', 'Business and Commercial', 'Business and commercial photography in Cape Town for teams, brands, and professional portraits.'),
  copy('lifestyle', 'Lifestyle', 'Lifestyle photography in Cape Town, from brand stories and yoga to everyday portraits.'),
  copy('natural', 'Natural', 'Natural and place photography in Cape Town, including farms, stays, and properties.'),
  copy('maternity', 'Maternity', 'Maternity photography in Cape Town by Carmen Lorraine. Pregnancy portraits made outdoors and at home.'),
  copy('newborn', 'Newborn', 'Newborn photography in Cape Town by Carmen Lorraine. Quiet portraits of new babies and their families.'),
  copy('weddings', 'Weddings', 'Wedding photography in Cape Town by Carmen Lorraine. Photographs of the day, the people, and the place.'),
];

CATALOG_FILTERS.forEach((filter) => {
  const intro = `${filter.name} photography in Cape Town by Carmen Lorraine.`;
  filter.h1 = filter.name;
  filter.title = `${filter.name} | Cape Town Photographer`;
  filter.description = intro;
  filter.intro = intro;
});

const GALLERY_CANONICAL_CATEGORY = {
  [GALLERY_ID.family]: 'family',
};

const GALLERY_TITLE = {
  [GALLERY_ID.family]: 'Family Photo Gallery - Carmen Lorraine Photography',
};

function withDefaultContent(filter) {
  const defaults = categoryContent.DEFAULT_CATEGORY_CONTENT[filter.slug];
  if (!defaults) return filter;
  const resolved = categoryContent.resolveCategoryFields(
    { slug: filter.slug },
    filter.name,
    filter.slug,
    filter.intro
  );
  return Object.assign({}, filter, resolved);
}

const GALLERY_FILTERS = SEMANTIC_FILTERS.concat(CATALOG_FILTERS).map(withDefaultContent);
const NAV_ORDER = [
  'family',
  'business-and-commercial',
  'lifestyle',
  'natural',
  'portrait',
  'events',
  'property',
  'wellness-community',
  'maternity',
  'newborn',
  'weddings',
];
const NAV_FILTERS = NAV_ORDER.map((slug) => GALLERY_FILTERS.find((filter) => filter.slug === slug)).filter(Boolean);
const FILTER_BY_SLUG = new Map(GALLERY_FILTERS.map((filter) => [filter.slug, filter]));
const CATEGORY_SLUGS = new Set(GALLERY_FILTERS.map((filter) => filter.slug));

const SLUG_ALIASES = {
  events: 'events',
  event: 'events',
  portrait: 'portrait',
  portraits: 'portrait',
  property: 'property',
  'business-and-commercial': 'business-and-commercial',
  'business-headshots': 'business-and-commercial',
  'business-and-headshots': 'business-and-commercial',
  business: 'business-and-commercial',
  commercial: 'business-and-commercial',
  'wellness-community': 'wellness-community',
  wellness: 'wellness-community',
  'travel-conservation': 'travel-conservation',
  family: 'family',
  lifestyle: 'lifestyle',
  natural: 'natural',
  maternity: 'maternity',
  newborn: 'newborn',
  weddings: 'weddings',
  wedding: 'weddings',
};

function slugify(value) {
  return String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
}

function barePath(value) {
  const text = String(value || '/').split('?')[0].split('#')[0];
  if (text.length > 1 && text.endsWith('/')) return text.slice(0, -1);
  return text || '/';
}

function gallerySlug(gallery, used) {
  const preferred = slugify(gallery && (gallery.slug || gallery.name || 'gallery'));
  let slug = preferred || 'gallery';
  if (used && used.has(slug)) {
    const suffix = String(gallery && gallery.id || '').slice(-4) || '2';
    slug = `${slug}-${suffix}`;
  }
  if (used) used.add(slug);
  return slug;
}

function withGallerySlugs(galleries) {
  const used = new Set();
  return (Array.isArray(galleries) ? galleries : []).map((gallery) => Object.assign({}, gallery, {
    slug: gallerySlug(gallery, used),
  }));
}

function galleryPath(gallery) {
  const slug = gallery && gallery.slug ? slugify(gallery.slug) : gallerySlug(gallery);
  return `/gallery/${slug || gallery.id}`;
}

function findGalleryByKey(galleries, key) {
  const raw = String(key || '');
  const list = withGallerySlugs(galleries);
  const byId = list.find((gallery) => String(gallery.id) === raw);
  if (byId) return byId;
  const slug = slugify(raw);
  if (!slug) return null;
  return list.find((gallery) => gallery.slug === slug) || null;
}

function categoryPath(slug) {
  const key = normalizeFilterSlug(slug) || slugify(slug);
  return key ? `/galleries/${key}` : '/galleries';
}

function normalizeFilterSlug(value) {
  const slug = slugify(value);
  if (!slug) return '';
  if (SLUG_ALIASES[slug]) return SLUG_ALIASES[slug];
  if (CATEGORY_SLUGS.has(slug)) return slug;
  return '';
}

function isGalleryCategoryPath(pathname) {
  const match = barePath(pathname).toLowerCase().match(/^\/galleries\/([^/]+)$/);
  return Boolean(match && normalizeFilterSlug(match[1]));
}

function filterForSlug(slug) {
  return FILTER_BY_SLUG.get(normalizeFilterSlug(slug)) || null;
}

function galleryCategorySlugs(gallery) {
  const values = [gallery && gallery.category].concat(
    Array.isArray(gallery && gallery.categories) ? gallery.categories : []
  );
  const seen = new Set();
  const slugs = [];
  values.forEach((value) => {
    const slug = slugify(value);
    if (!slug || seen.has(slug)) return;
    seen.add(slug);
    slugs.push(slug);
  });
  return slugs;
}

function storedCategoryMatches(stored, key) {
  if (stored === key) return true;
  return (LEGACY_CATEGORY_MATCH[key] || []).includes(stored);
}

function galleryInFilter(gallery, slug) {
  const key = normalizeFilterSlug(slug) || slugify(slug);
  if (!key || !gallery) return false;
  const semantic = SEMANTIC_IDS[key];
  if (semantic && semantic.includes(String(gallery.id))) return true;
  return galleryCategorySlugs(gallery).some((stored) => storedCategoryMatches(stored, key));
}

function firstParagraph(value) {
  return String(value || '')
    .split(/\n+/)
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .find(Boolean) || '';
}

function filterFromCategory(category) {
  const name = String(category && category.name || '').trim();
  const slug = slugify(category && category.slug);
  const introSource = firstParagraph(category && category.description)
    || `${name} photography in Cape Town by Carmen Lorraine.`;
  const resolved = categoryContent.resolveCategoryFields(
    Object.assign({}, category, { slug: slug }),
    name,
    slug,
    introSource
  );
  return Object.assign({ slug: slug, name: name, fromCategory: true }, resolved);
}

function galleryCanonicalPath(gallery) {
  const category = GALLERY_CANONICAL_CATEGORY[String(gallery && gallery.id)];
  if (category) return `/galleries/${category}`;
  return galleryPath(gallery);
}

function galleryDisplayTitle(gallery) {
  return GALLERY_TITLE[String(gallery && gallery.id)] || '';
}

function findStoredCategory(categories, slug) {
  const key = slugify(slug);
  const list = Array.isArray(categories) ? categories : [];
  if (!key) return null;
  const direct = list.find((category) => slugify(category && category.slug) === key);
  if (direct) return direct;
  const previous = list.find((category) => (
    Array.isArray(category && category.previousSlugs)
    && category.previousSlugs.some((item) => slugify(item) === key)
  ));
  if (previous) return previous;
  const legacyId = LEGACY_SLUG_TO_ID[key];
  if (!legacyId) return null;
  return list.find((category) => String(category && category.id) === legacyId) || null;
}

/**
 * Public category page for a URL slug. Admin category name and slug win.
 * A known older slug comes back as the current category so the route can redirect.
 */
function resolveGalleryCategory(rawSlug, categories) {
  const requested = slugify(rawSlug);
  if (!requested) return null;
  const stored = findStoredCategory(categories, requested);
  if (stored && slugify(stored.slug)) return filterFromCategory(stored);
  return filterForSlug(requested);
}

function sitemapFilters(categories, galleries) {
  const pages = [];
  const seen = new Set();
  const list = Array.isArray(categories) ? categories : [];
  list.forEach((category) => {
    const page = resolveGalleryCategory(category && category.slug, list);
    if (!page || !page.slug || seen.has(page.slug)) return;
    if (!galleriesForFilter(galleries, page.slug).length) return;
    seen.add(page.slug);
    (category.previousSlugs || []).forEach((item) => seen.add(slugify(item)));
    Object.keys(LEGACY_SLUG_TO_ID).forEach((alias) => {
      if (String(category.id) === LEGACY_SLUG_TO_ID[alias]) seen.add(alias);
    });
    pages.push(page);
  });
  GALLERY_FILTERS.forEach((filter) => {
    if (seen.has(filter.slug)) return;
    if (!galleriesForFilter(galleries, filter.slug).length) return;
    seen.add(filter.slug);
    pages.push(filter);
  });
  return pages;
}

function galleriesForFilter(galleries, slug) {
  const list = withGallerySlugs(galleries).filter((gallery) => gallery && gallery.published !== false);
  if (!slug) return list;
  return list.filter((gallery) => galleryInFilter(gallery, slug));
}

function slugByGalleryId(galleries) {
  const map = new Map();
  withGallerySlugs(galleries).forEach((gallery) => {
    map.set(String(gallery.id), gallery.slug);
  });
  return map;
}

function isArchivePath(pathname) {
  return /^\/(galleries|category|tag|gallerycat|portfolios|portfoliosets)\//.test(barePath(pathname).toLowerCase());
}

/**
 * Final public path for a legacy URL. Null means the path is now a real page
 * and must not redirect.
 */
function retargetLegacy(from, to, slugMap) {
  const path = barePath(from).toLowerCase();
  const dest = barePath(to);
  if (isGalleryCategoryPath(path)) return null;

  if (isArchivePath(path)) {
    if (/maternity|beautiful-bellies|pregnan/.test(path)) return '/galleries/maternity';
    if (/newborn/.test(path)) return '/galleries/newborn';
    if (/wedding/.test(path)) return '/galleries/weddings';
    if (/family|children/.test(path)) return '/galleries/family';
    if (/head-?shots?|corporate|business-portrait|business-photography|business-head/.test(path)) {
      return '/galleries/business-and-commercial';
    }
    if (/lifestyle/.test(path)) return '/galleries/lifestyle';
    if (/yoga/.test(path)) return `/gallery/${slugMap.get(GALLERY_ID.yoga) || 'yoga'}`;
    if (/nature|landscape|horse|farm|conservation/.test(path) && dest.startsWith('/gallery/')) {
      return '/galleries/natural';
    }
  }

  const numeric = dest.match(/^\/gallery\/(\d+)$/);
  if (numeric) {
    const slug = slugMap.get(numeric[1]);
    if (slug) return `/gallery/${slug}`;
  }
  return dest || '/';
}

function canonicalRequest(pathname) {
  let value = String(pathname || '/');
  try {
    value = decodeURIComponent(value);
  } catch (error) {
    value = String(pathname || '/');
  }
  const lower = value.toLowerCase();
  const bare = barePath(lower);
  const gallery = bare.match(/^\/gallery\/([^/]+)$/);
  if (gallery && /^\d+$/.test(gallery[1])) return null;
  const interesting = bare === '/blog'
    || bare === '/galleries'
    || bare === '/about'
    || bare === '/contact'
    || bare === '/services'
    || bare === '/quote'
    || /^\/services\/[a-z0-9-]+$/.test(bare)
    || /^\/blog\/(?:page\/\d+|category\/[a-z0-9-]+(?:\/page\/\d+)?|feed\.xml|preview\/[a-z0-9-]+|[a-z0-9-]+)$/.test(bare)
    || isGalleryCategoryPath(bare)
    || /^\/gallery\/[a-z0-9-]+$/.test(bare);
  if (!interesting) return null;
  if (value === bare) return null;
  // Newly folded pages keep their existing trailing-slash redirect.
  // /Blog already folds case here; /about/ must stay on the relative slash redirect.
  const caseOnly = bare === '/about'
    || bare === '/contact'
    || bare === '/services'
    || bare === '/quote'
    || /^\/services\/[a-z0-9-]+$/.test(bare);
  if (caseOnly && barePath(value) === bare) return null;
  return bare;
}

module.exports = {
  SITE,
  GALLERY_ID,
  GALLERY_FILTERS,
  NAV_FILTERS,
  SEMANTIC_FILTERS,
  CATALOG_FILTERS,
  CATEGORY_SLUGS,
  slugify,
  barePath,
  gallerySlug,
  withGallerySlugs,
  galleryPath,
  galleryCanonicalPath,
  galleryDisplayTitle,
  GALLERY_CANONICAL_CATEGORY,
  findGalleryByKey,
  categoryPath,
  normalizeFilterSlug,
  isGalleryCategoryPath,
  filterForSlug,
  resolveGalleryCategory,
  sitemapFilters,
  galleryCategorySlugs,
  galleryInFilter,
  galleriesForFilter,
  slugByGalleryId,
  retargetLegacy,
  canonicalRequest,
};
